export const endPoint = {
  // VENDOR PORTAL
  portal: {
    // PublicAxiosInstance (baseURL already includes /public)
    invitationValidate: '/service-provider/invitation/validate',
    invitationClaim: '/service-provider/invitation/claim',
    // PrivateAxiosInstance (authenticated vendor)
    claimExisting: '/portal/invitation/claim-existing',
    // Assessments assigned to the vendor's associated supply chain(s)
    assessment: '/portal/assessment',
    assessmentSaveDraft: (id: string) => `/portal/assessment/${id}/save-draft`,
    assessmentSubmit: (id: string) => `/portal/assessment/${id}/submit`,
    assessmentAnswerTemplate: (id: string) => `/portal/assessment/${id}/answer-template`,
    assessmentAnswerUpload: (id: string) => `/portal/assessment/${id}/answer-upload`,
    assessmentCollaborators: (id: string) => `/portal/assessment/${id}/collaborators`,
    assessmentCollaboratorRevoke: (id: string) => `/portal/assessment/${id}/collaborators/revoke`,
    assessmentCollaboratorExpiry: (id: string) => `/portal/assessment/${id}/collaborators/expiry`,
    // CTRA questionnaire batch answering (multiple independent recipients per batch)
    ctraAssessment: (id: string) => `/portal/ctra-assessment/${id}`,
    ctraAssessmentSaveDraft: (id: string) => `/portal/ctra-assessment/${id}/save-draft`,
    ctraAssessmentSubmit: (id: string) => `/portal/ctra-assessment/${id}/submit`,
  },
  // LEGACY SERVICE-PROVIDER ASSESSMENT (PublicAxiosInstance - baseURL includes /public)
  serviceProviderAssessmentPublic: {
    // Bridge an old shareable secure link into the vendor-portal invitation flow
    migrate: (token: string) => `/service-provider-assessment/migrate/${token}`,
  },
  // COMMON
  me: '/auth/me',
  organisationAndPermissions: '/auth/organisations-and-permissions',
  loginLogs: '/auth/login-logs',
  changePassword: '/auth/change-password',
  logout: '/auth/logout',
  sessions: '/auth/sessions',
  passwordPolicyByEmail: '/auth/password-policy-by-email',
  userPreference: '/user-preferences',
  userGuides: '/user-guides',
  defaultOrganisation: '/user-preferences/default-organisation',
  activityLogs: '/activities',
  helpfulTips: '/helpful-tips',
  bugReport: '/bug-report',
  bugReportActivities: '/bug-report/activities',
  signupRequest: '/signup-request',
  uploads: {
    signedUrl: '/uploads/signed-url',
    delete: '/uploads/delete',
  },
  zapierWebhook: '/webhook/zapier',
  n8nWebhook: '/webhook/n8n',
  assistant: {
    chatStream: '/assistant/chat/stream',
    conversations: '/assistant/conversations',
    messageReaction: (id: string) => `/assistant/messages/${id}/reaction`,
  },
  userManual: '/user-manual',

  // ORGANISATION
  organisation: {
    dashboards: '/organisation/dashboards',
    deltaReport: '/organisation/delta-report',
    mailLogs: '/organisation/mail-logs',
    logo: '/organisation/manage-organisation/logo',
    user: '/organisation/user',
    apiKeys: '/organisation/api-keys',
    aiModel: '/organisation/ai-model',
    aiModelDiscoverModels: '/organisation/ai-model/discover-models',
    ai: {
      ingestEvidence: '/organisation/ai/ingest-evidence',
      standardQA: '/organisation/ai/standard-qa',
      gapAssessment: '/organisation/ai/gap-assessment',
      ingestedFileIds: '/organisation/ai/ingested-file-ids',
      documentControls: '/organisation/ai/document-controls',
      attachEvidence: '/organisation/ai/attach-evidence',
      complianceCheck: '/organisation/ai/compliance-check',
      controlSummary: '/organisation/ai/control-summary',
    },
    tts: {
      request: '/organisation/tts',
      status: (contentHash: string) => `/organisation/tts/${contentHash}`,
    },
    mcpServerExtension: '/organisation/mcp-server-extension',
    sharepointConfig: '/organisation/sharepoint-config',
    googleDrive: {
      config: '/organisation/google-drive',
      status: '/organisation/google-drive/status',
      authorize: '/organisation/google-drive/authorize',
      callback: '/organisation/google-drive/callback',
      disconnect: '/organisation/google-drive/disconnect',
    },
    globalSearch: '/organisation/global-search',
    emailConfig: {
      config: '/organisation/email-config',
      status: '/organisation/email-config/status',
      test: '/organisation/email-config/test',
      authorize: '/organisation/email-config/authorize',
      callback: '/organisation/email-config/callback',
    },
    classificationLabel: '/organisation/classification-label',
    organisationUser: '/organisation/organisation-user',
    userList: '/organisation/user/list',
    resetUserPassword: '/organisation/user/reset-password',
    releaseNotes: '/organisation/release-notes',
    roleDefaultPermission: '/organisation/role-default-permission',
    manageOrganisation: '/organisation/manage-organisation',
    childOrganisation: '/organisation/manage-organisation/child-organisation',
    organisationRequest: '/organisation/manage-organisation/request',
    passwordPolicy: '/organisation/password-policy',
    onboardingAutoSetupStream: '/organisation/onboarding/auto-setup/stream',
    enterpriseConnection: '/organisation/enterprise-connection',
    staff: '/organisation/staff',
    uploadStaffExcel: '/organisation/staff/upload',
    department: '/organisation/department',
    defaultPolicyReviewer: '/organisation/default-policy-reviewer',
    tags: {
      tags: '/organisation/tags',
      list: '/organisation/tags/list',
      assign: '/organisation/tags/assign',
      entity: '/organisation/tags/entity',
      links: '/organisation/tags/links',
    },
    /** Generic comment thread shared by every commentable entity - see IEntityComment */
    comments: '/organisation/comments',
    organisationPreferences: '/organisation/organisation-preferences',
    fieldRequirements: '/organisation/field-requirements',
    toasterSetting: '/organisation/toaster-setting',
    documentPdfTemplate: {
      base: '/organisation/document-pdf-template',
      byType: (type: string) => `/organisation/document-pdf-template/${type}`,
      logo: (type: string) => `/organisation/document-pdf-template/${type}/logo`,
    },
    zapierWebhook: '/organisation/zapier-webhook',
    n8nWebhook: '/organisation/n8n-webhook',
    generateRoleAndResponsibilities: '/organisation/staff/generate-role-and-responsibilities',
    generateStaff: '/organisation/staff/ai/generate-staff',
    generateStaffStream: '/organisation/staff/ai/generate-staff/stream',
    assignUserToStaff: '/organisation/staff/assign-user',
    removeUserFromStaff: '/organisation/staff/remove-user',
    resource: '/organisation/resources',
    externalIntegration: '/organisation/external-integration',
    activityReports: {
      overview: '/organisation/activity-reports/overview',
      comprehensive: '/organisation/activity-reports/comprehensive',
      activeUsers: '/organisation/activity-reports/active-users',
      moduleUsage: '/organisation/activity-reports/modules/usage',
      unusedModules: '/organisation/activity-reports/modules/unused',
      licensedUnused: '/organisation/activity-reports/modules/licensed-unused',
      users: '/organisation/activity-reports/users',
      trends: '/organisation/activity-reports/trends',
      peakUsage: '/organisation/activity-reports/peak-usage',
      export: '/organisation/activity-reports/export',
    },
    aiToken: {
      aiTokenStatus: '/organisation/ai-tokens/stats',
      aiTokenUsageHistory: '/organisation/ai-tokens/usage-history',
      aiTokenByOperation: '/organisation/ai-tokens/by-operation',
    },

    jira: {
      testConnection: '/organisation/jira/test-connection',
      config: '/organisation/jira/config',
      projects: '/organisation/jira/projects',
      statuses: '/organisation/jira/statuses',
      projectMappings: '/organisation/jira/project-mappings',
      statusMappings: '/organisation/jira/status-mappings',
      projectToGrcMappings: '/organisation/jira/project-to-grc-mappings',
      sync: {
        all: '/organisation/jira/sync/all',
        entity: '/organisation/jira/sync',
        board: '/organisation/jira/sync-board',
      },
    },
    azureIntegration: {
      testConnection: '/organisation/azure-integration/test-connection',
      config: '/organisation/azure-integration',
      staffList: '/organisation/azure-integration/staff-list',
      azureUsersSync: '/organisation/azure-integration/azure-users-sync',
    },
    confluenceIntegration: {
      testConnection: '/organisation/confluence-integration/test-connection',
      config: '/organisation/confluence-integration',
    },
    msDefenderIntegration: {
      testConnection: '/organisation/ms-defender-integration/test-connection',
      config: '/organisation/ms-defender-integration',
    },
    vantaIntegration: {
      testConnection: '/organisation/vanta-integration/test-connection',
      config: '/organisation/vanta-integration',
    },
    drataIntegration: {
      testConnection: '/organisation/drata-integration/test-connection',
      config: '/organisation/drata-integration',
    },
    paloAltoNetworksIntegration: {
      testConnection: '/organisation/palo-alto-networks-integration/test-connection',
      config: '/organisation/palo-alto-networks-integration',
    },
    mimecastIntegration: {
      testConnection: '/organisation/mimecast-integration/test-connection',
      config: '/organisation/mimecast-integration',
    },
    meetingMinutes: {
      meetingMinutes: '/organisation/meeting-minutes',
      attendeesList: '/organisation/meeting-minutes/attendees-list',
      sendReminderEmail: '/organisation/meeting-minutes/send-reminder-email',
      generateMeetingAgendaUsingAI: '/organisation/meeting-minutes/ai/generate-meeting-agenda',
      generateMeetingMinutesUsingAI: '/organisation/meeting-minutes/ai/generate-meeting-minutes',
      generateMeetingAgendaUsingAIStream: '/organisation/meeting-minutes/ai/generate-meeting-agenda/stream',
      generateMeetingMinutesUsingAIStream: '/organisation/meeting-minutes/ai/generate-meeting-minutes/stream',
      generateMeetingMinute: '/organisation/meeting-minutes/ai/generate-meeting-minute',
      generateMeetingMinuteStream: '/organisation/meeting-minutes/ai/generate-meeting-minute/stream',
      meetingMinutesType: '/organisation/meeting-minutes/type',
      sendMinutesToAttendees: '/organisation/meeting-minutes/send-minutes-to-attendees',
      sendMinutesForReview: '/organisation/meeting-minutes/send-minutes-for-review',
      reviewHistory: '/organisation/meeting-minutes/review-history',
      askForReviewAgain: '/organisation/meeting-minutes/ask-for-review-again',
      meetingMinuteHistory: '/organisation/meeting-minutes/history',
      reviewReport: '/organisation/meeting-minutes/review-report',
    },
    changeManagementProcess: {
      changeManagementProcesses: '/organisation/change-management-process',
      generate: '/organisation/change-management-process/ai/generate',
      generateStream: '/organisation/change-management-process/ai/generate/stream',
      review: '/organisation/change-management-process/review',
      approve: '/organisation/change-management-process/approve',
      category: '/organisation/change-management-process/category',
      expiredStaffLinks: '/organisation/change-management-process/expired-staff-links',
      migrateExpiredStaffLinks: '/organisation/change-management-process/migrate-expired-staff-links',
    },
    project: {
      project: '/organisation/project',
      projectName: '/organisation/project/name',
      withOrganisedTask: '/organisation/project/tasks-organised',
      pciDssTasksOrganised: '/organisation/project/pci-dss/tasks-organised',
      report: '/organisation/project/report',
      aiGenerateRelatedTasksStream: '/organisation/project/ai/generate-related-tasks/stream',
      createPciDssProjectWithTasks: '/organisation/project/pci-dss/create-project-with-tasks',
      createPciDssProjectWithEvidencePacks: '/organisation/project/pci-dss/create-project-with-evidence-packs',
      pciDssReport: '/organisation/project/pci-dss/report',
    },
    informationAsset: {
      informationAsset: '/organisation/information-asset',
      uploadExcel: '/organisation/information-asset/upload',
      generate: '/organisation/information-asset/ai/generate',
      generateStream: '/organisation/information-asset/ai/generate/stream',
      assetType: '/organisation/information-asset/asset-type',
      runZero: '/organisation/information-asset/run-zero',
      moveRunZeroAssetToInformationAsset: '/organisation/information-asset/move-run-zero-asset-to-information-asset',
      moveRunZeroAssetsToInformationAssets:
        '/organisation/information-asset/move-run-zero-assets-to-information-assets',
      report: {
        type: '/organisation/information-asset/report/type',
        confidentiality: '/organisation/information-asset/report/confidentiality',
        integrity: '/organisation/information-asset/report/integrity',
        businessImpact: '/organisation/information-asset/report/business-impact',
        report: '/organisation/information-asset/report',
        systemExposure: '/organisation/information-asset/report/system-exposure',
        hostingModel: '/organisation/information-asset/report/hosting-model',
        dataClassification: '/organisation/information-asset/report/data-classification',
        userBase: '/organisation/information-asset/report/user-base',
        department: '/organisation/information-asset/report/department',
      },
    },

    module: {
      moduleList: '/organisation/module/list',
      organisationModule: '/organisation/module/organisation-module',
    },

    compliance: {
      nistCSFReport: {
        summary: '/organisation/compliance/nist-csf-report/summary',
        detail: '/organisation/compliance/nist-csf-report/detail',
        comparison: '/organisation/compliance/nist-csf-report/comparison',
      },
      cspPortfolio: {
        portfolio: '/organisation/compliance/csp-portfolio',
        company: '/organisation/compliance/csp-portfolio',
      },
      report: {
        assessmentCompletion: '/organisation/compliance/report/assessment-completion',
        assessmentComplianceStatus: '/organisation/compliance/report/assessment-compliance-status',
        standardDashboard: '/organisation/compliance/report/standard-dashboard',
        standardDashboardDailyTrend: '/organisation/compliance/report/standard-dashboard/daily-trend',
      },
      pci: {
        assessedEntitySection: '/organisation/compliance/pci',
        businessOverview: '/organisation/compliance/pci',
        credentialRegistry: '/organisation/compliance/pci/credential-registry',
        scopeOfWork: '/organisation/compliance/pci',
        reviewedEnvironments: '/organisation/compliance/pci',
        quarterlyScanResult: '/organisation/compliance/pci',
        samplingAndEvidence: '/organisation/compliance/pci',
        generateRocReport: '/organisation/compliance/pci/pci-report/roc/generate',
        generateSaqReport: '/organisation/compliance/pci/pci-report/saq/generate',
        evidenceInfo: '/organisation/compliance/pci/evidence-info',

        saq: {
          configuration: {
            saqConfig: '/organisation/compliance/pci/saq/configuration',
            assessedMerchant: '/organisation/compliance/pci/saq/configuration/assessed-merchant',
            assessors: '/organisation/compliance/pci/saq/configuration/assessors',
            eligibilityToCompleteSaq: '/organisation/compliance/pci/saq/configuration/eligibility-to-complete-saq',
            inScopeLocations: '/organisation/compliance/pci/saq/configuration/in-scope-locations',
            merchantBusinessPaymentChannel:
              '/organisation/compliance/pci/saq/configuration/merchant-business-payment-channel',
            paymentCardEnvironment: '/organisation/compliance/pci/saq/configuration/payment-card-environment',
            pciSaqThirdPartyServiceProviders:
              '/organisation/compliance/pci/saq/configuration/pci-saq-third-party-service-providers',
            roleWithPaymentCard: '/organisation/compliance/pci/saq/configuration/role-with-payment-card',
            sscValidatedProductsAndSolutions:
              '/organisation/compliance/pci/saq/configuration/ssc-validated-products-and-solutions',
          },
          acknowledgement: {
            saqAcknowledgement: '/organisation/compliance/pci/saq/acknowledgement',
            actionPlanForSaqNCRequirement:
              '/organisation/compliance/pci/saq/acknowledgement/action-plan-for-saq-nc-requirement',
            isaInvolvement: '/organisation/compliance/pci/saq/acknowledgement/isa-involvement',
            merchantAcknowledgement: '/organisation/compliance/pci/saq/acknowledgement/merchant-acknowledgement',
            merchantAttestation: '/organisation/compliance/pci/saq/acknowledgement/merchant-attestation',
            qsaAcknowledgement: '/organisation/compliance/pci/saq/acknowledgement/qsa-acknowledgement',
          },
        },
      },
      ismIrap: {
        base: '/organisation/compliance/ism-irap',
        introduction: '/organisation/compliance/ism-irap',
        documentDetails: '/organisation/compliance/ism-irap',
        systemOverview: '/organisation/compliance/ism-irap',
        administrativeAndSupportEnvironment: '/organisation/compliance/ism-irap',
        testDevProdEnvironment: '/organisation/compliance/ism-irap',
        detailedFindings: '/organisation/compliance/ism-irap',
        generateReport: '/organisation/compliance/ism-irap/report/generate',
      },
      pciScoping: {
        pciScoping: '/organisation/compliance/assessment/pci-scoping',
        cde: '/organisation/compliance/assessment/pci-scoping/cde',
        people: '/organisation/compliance/assessment/pci-scoping/people',
        peopleAccessType: '/organisation/compliance/assessment/pci-scoping/people/access-type',
        peopleTraining: '/organisation/compliance/assessment/pci-scoping/people/training',
        process: '/organisation/compliance/assessment/pci-scoping/process',
        processDataHandled: '/organisation/compliance/assessment/pci-scoping/process/data-handled',
        processFrequency: '/organisation/compliance/assessment/pci-scoping/process/frequency',
        system: '/organisation/compliance/assessment/pci-scoping/system',
        systemInformationAsset: '/organisation/compliance/assessment/pci-scoping/system/information-asset',
        systemServiceProvider: '/organisation/compliance/assessment/pci-scoping/system/service-provider',
        segmentation: '/organisation/compliance/assessment/pci-scoping/segmentation',
        signOff: '/organisation/compliance/assessment/pci-scoping/sign-off',
        rephraseRemarks: '/organisation/compliance/assessment/pci-scoping/sign-off/ai/rephrase-remarks',
      },
      ismScoping: {
        ismScoping: '/organisation/compliance/assessment/ism-scoping',
        signOff: '/organisation/compliance/assessment/ism-scoping/sign-off',
        introduction: '/organisation/compliance/assessment/ism-scoping/introduction',
      },
    },

    nonConformity: {
      nonConformity: '/organisation/compliance/non-conformance',
      archived: '/organisation/compliance/non-conformance/archived',
      aiGenerateTasks: '/organisation/compliance/non-conformance/ai/generate-tasks',
      generate: '/organisation/compliance/non-conformance/ai/generate',
      generateStream: '/organisation/compliance/non-conformance/ai/generate/stream',
      restore: '/organisation/compliance/non-conformance/restore',

      actionStatus: '/organisation/non-conformity/action-status',
      importance: '/organisation/non-conformity/importance',
      affectedAreas: '/organisation/non-conformity/affected-areas',
      rootIdentification: '/organisation/non-conformity/root-identification',
    },

    ofi: {
      ofi: '/organisation/compliance/ofi',
      archived: '/organisation/compliance/ofi/archived',
      aiGenerateTasks: '/organisation/compliance/ofi/ai/generate-tasks',
      generate: '/organisation/compliance/ofi/ai/generate',
      generateStream: '/organisation/compliance/ofi/ai/generate/stream',
      restore: '/organisation/compliance/ofi/restore',
    },

    statementOfApplicability: {
      statementOfApplicability: '/organisation/compliance/statement-of-applicability',
      statementOfApplicabilityForIsm: '/organisation/compliance/statement-of-applicability/ism',
      approveStatementOfApplicabilityForIsm: '/organisation/compliance/statement-of-applicability/approve-for-ism',
      soaInControlHierarchy: '/organisation/compliance/statement-of-applicability/soa-in-control-hierarchy',
      soaInControlHierarchyForIsm:
        '/organisation/compliance/statement-of-applicability/soa-in-control-hierarchy-for-ism',
      // SoA document control (all standards except the ISM)
      versionStatus: '/organisation/compliance/statement-of-applicability/version/status',
      versionList: '/organisation/compliance/statement-of-applicability/version/list',
      version: '/organisation/compliance/statement-of-applicability/version',
      approve: '/organisation/compliance/statement-of-applicability/version/approve',
      approvers: '/organisation/compliance/statement-of-applicability/approvers',
      generateJustifications: '/organisation/compliance/statement-of-applicability/justification/generate',
      acceptJustifications: '/organisation/compliance/statement-of-applicability/justification/accept',
    },

    standard: {
      standardList: '/organisation/standard/list',
      onboardedStandardList: '/organisation/standard/onboarded-list',
      organisationStandards: '/organisation/standard/organisation-standards',
      getOrganisationNameById: '/organisation/standard/organisation-standards/get-name',
      getControlNameById: '/organisation/standard/organisation-standards/get-control-name',
      standardWithControls: '/organisation/standard/hierarchy-with-controls',
      organisationStandardsList: '/organisation/standard/organisation-standards/list',
      organisationStandardsForAssessmentCards: '/organisation/standard/organisation-standards/assessment-cards',
      organisationStandardControlHierarchy: '/organisation/standard/organisation-standards/control-hierarchy',
      organisationStandardControlHierarchyByStaticValues:
        '/organisation/standard/organisation-standards/control-hierarchy-by-static-values',
      organisationControlsToEditInHierarchy:
        '/organisation/standard/organisation-standards/controls-to-edit-in-hierarchy',
      organisationStandardAssessedControlHierarchy:
        '/organisation/standard/organisation-standards/assessed-control-hierarchy',
      organisationStandardIsmIrapAssessedControlHierarchy:
        '/organisation/standard/organisation-standards/ism-irap-assessed-control-hierarchy',
      organisationStandardIsmPrincipleAssessmentHierarchy:
        '/organisation/standard/organisation-standards/ism-principle-assessment-hierarchy',
      organisationStandardAuditControlHierarchy:
        '/organisation/standard/organisation-standards/audit-control-hierarchy',
      organisationStandardReviewedControlHierarchy:
        '/organisation/standard/organisation-standards/reviewed-control-hierarchy',
      organisationStandardControlDetails: '/organisation/standard/organisation-standards/control-details',
      organisationStandardMaturityLevelList: '/organisation/standard/organisation-standards/maturity-level-list',
      essentialEightMaturityLevelList:
        '/organisation/standard/organisation-standards/essential-eight-maturity-level-list',
      standardLevels: '/organisation/standard/standard-levels/levels',
      standardLevelsList: '/organisation/standard/standard-levels/list',
      saqTypes: '/organisation/standard/saq-types',
      saqTypeNames: '/organisation/standard/saq-types/saq-type-names',
    },

    staticAttributes: {
      ismAttributes: '/organisation/static-attributes/ism-attributes',
      staticValueList: '/organisation/static-attributes/static-value-list',
    },

    control: {
      control: '/organisation/control',
      controlList: '/organisation/control/list',
      iso270012022ControlList: '/organisation/control/iso-27001-2022/list',
      controlGroupList: '/organisation/control-group/list',
      controlQuestionnaire: '/organisation/control/questionnaires',
      assessmentQuestionnaireResponse: '/organisation/control/responses/questionnaire',
      assessmentQuestionnaireWithResponse: '/organisation/control/questionnaire_response',
      completeAssessmentQuestionnaire: '/organisation/control/complete-assessment',
    },

    assessment: {
      assessment: '/organisation/compliance/assessment',
      assessmentwithAssessmentDetails: '/organisation/compliance/assessment/with-assessment-details',
      pciAssessmentwithAssessmentDetails: '/organisation/compliance/assessment/with-pci-assessment-details',
      pciSaqAssessmentAssessmentDetails: '/organisation/compliance/assessment/pci-saq-assessment-details',
      generateTargetedRisk: '/organisation/compliance/assessment/generate-targeted-risk',
      pciRocAiAssessment: '/organisation/compliance/assessment/pci-roc-ai-assessment',
      pciRocAiAssessmentStream: '/organisation/compliance/assessment/pci-roc-ai-assessment/stream',
      pciSaqAiAssessment: '/organisation/compliance/assessment/pci-saq-ai-assessment',
      pciSaqAiAssessmentStream: '/organisation/compliance/assessment/pci-saq-ai-assessment/stream',
      silAssessmentwithAssessmentDetails: '/organisation/compliance/assessment/with-sil-assessment-details', // for SIL assessment detail
      ismIrapAssessmentwithAssessmentDetails: '/organisation/compliance/assessment/with-ism-irap-assessment-details', // for ISM IRAP assessment detail
      ismPrinciplesAssessmentWithAssessmentDetails:
        '/organisation/compliance/assessment/with-ism-principles-assessment-details',
      closeAssessment: '/organisation/compliance/assessment/close-assessment',
      activeAssessments: '/organisation/compliance/assessment/active-assessment',
      startAssessment: '/organisation/compliance/assessment/start-assessment',
      startIsmGapAssessment: '/organisation/compliance/assessment/start-ism-gap-assessment',
      startPciAssessment: '/organisation/compliance/assessment/start-pci-assessment',
      selectedSaqTypesByOrganisationStandardIdActiveAssessment:
        '/organisation/compliance/assessment/selected-saq-types-by-organisation-standard-id-active-assessment',
      updateSelectedSaqTypesOfAssessment: '/organisation/compliance/assessment/update-selected-saq-types-of-assessment',
      startPciSaqAssessment: '/organisation/compliance/assessment/start-pci-saq-assessment',
      editAssessmentControls: '/organisation/compliance/assessment/edit-assessment-controls',
      requestForAudit: '/organisation/compliance/assessment/request-audit',
      cancelAudit: '/organisation/compliance/assessment/cancel-audit',
      updateAuditAuditors: '/organisation/compliance/assessment/update-audit-auditors',
      canEditControls: '/organisation/compliance/assessment/can-edit-controls',
      nswCspAllAssessmentDetailsAssessed: '/organisation/compliance/assessment/nsw-csp/all-assessment-details-assessed',
    },

    assessmentDetails: {
      base: '/organisation/compliance/assessment-detail',
      assessmentStatusByControlId: '/organisation/compliance/assessment-detail/assessment-status', // to get data for assessment status stepper form in compliance
      assessmentStatusIsmPrinciple: '/organisation/compliance/assessment-detail/assessment-status-ism-principle',
      assessmentStatusForAuditorByControlId: '/organisation/compliance/assessment-detail/assessment-status-for-auditor', // to get data for assessment status stepper form in compliance
      auditorAiSummary: '/organisation/compliance/assessment-detail/auditor/ai-summary', // AI summary of the responses an auditor is reviewing
      relatedControls: '/organisation/compliance/assessment-detail/related-controls',
      toRelateControls: '/organisation/compliance/assessment-detail/to-relate-controls',
      aiDraft: '/organisation/compliance/assessment-detail/ai-draft',
      aiDraftUpdate: '/organisation/compliance/assessment-detail/ai-draft',
      aiDraftApprove: '/organisation/compliance/assessment-detail/ai-draft/approve',
      aiDraftReject: '/organisation/compliance/assessment-detail/ai-draft/reject',
      aiAssessmentReset: '/organisation/compliance/assessment-detail/ai-assessment/reset',
      policyDetailsForAuditorByAssessmentDetailId: '/organisation/compliance/assessment-detail/auditor/policy',
      staffDetailsForAuditorByAssessmentDetailId: '/organisation/compliance/assessment-detail/auditor/staff',
      informationAssetDetailsForAuditorByAssessmentDetailId:
        '/organisation/compliance/assessment-detail/auditor/information-asset',
      cspRiskDetailsForAuditorByAssessmentDetailId: '/organisation/compliance/assessment-detail/auditor/csp-risk',
      cspExceptionDetailsForAuditorByAssessmentDetailId:
        '/organisation/compliance/assessment-detail/auditor/csp-exception',
    },

    assessmentDetailsControl: {
      assessmentDetailsControl: '/organisation/compliance/assessment-detail-control',
    },

    assessmentClosing: {
      assessmentClosing: '/organisation/compliance/assessment-closing',
      assessmentClosingList: '/organisation/compliance/assessment-closing/list',
      assessmentClosingAll: '/organisation/compliance/assessment-closing/all',
    },

    assessmentLogs: {
      assessmentLogs: '/organisation/compliance/assessment-detail-logs',
    },

    assessmentReport: {
      reporting: '/organisation/compliance/assessment-report/reporting/initial-data',
      chartData: '/organisation/compliance/assessment-report/reporting/chart-data',
      generateReport: '/organisation/compliance/assessment-report/reporting/generate',
      generateRecommendations: '/organisation/compliance/assessment-report/reporting/generate-recommendations',
      assessmentReports: '/organisation/compliance/assessment-report/assessment-reports',
      generatingAssessmentReport: '/organisation/compliance/assessment-report/generating-report',
      downloadReport: '/organisation/compliance/assessment-report/assessment-reports',
      cancelGeneration: '/organisation/compliance/assessment-report/assessment-reports',
    },

    audit: {
      audit: '/organisation/compliance/audit',
      auditByControlId: '/organisation/compliance/audit/control',
      rephraseRemarks: '/organisation/compliance/audit/ai/rephrase-remarks',
      auditsByOrganisationStandardId: '/organisation/compliance/audit/organisation-standard',
      changeAuditStatus: '/organisation/compliance/audit/change-status',
      auditLogsWithAssessmentDetails: '/organisation/compliance/audit',
    },

    auditDetails: {
      auditDetails: '/organisation/compliance/audit-detail',
      auditDetailsByControlId: '/organisation/compliance/audit-detail/control',
      complianceStatus: '/organisation/compliance/audit-detail/compliance-status',
    },

    cspReviewDetail: {
      reviewDetail: '/organisation/compliance/csp-review-detail',
      reviewDetailByControlId: '/organisation/compliance/csp-review-detail/control',
      reviewRemarksByControlId: '/organisation/compliance/csp-review-detail',
    },

    cspAssessmentReview: {
      summary: '/organisation/compliance/csp-assessment-review/summary',
      complete: '/organisation/compliance/csp-assessment-review/complete',
      signOff: '/organisation/compliance/csp-assessment-review/sign-off',
    },

    cspAttestation: {
      base: '/organisation/compliance/csp-attestation',
      signOff: '/organisation/compliance/csp-attestation/sign-off',
      requestChanges: '/organisation/compliance/csp-attestation/request-changes',
    },

    cspAttestationReport: {
      base: '/organisation/compliance/csp-attestation-report',
      generate: '/organisation/compliance/csp-attestation-report/generate',
      score: '/organisation/compliance/csp-attestation-report/score',
    },

    cspExceptionRequest: {
      base: '/organisation/compliance/csp-exception-request',
      approve: '/organisation/compliance/csp-exception-request/approve',
      reject: '/organisation/compliance/csp-exception-request/reject',
      link: '/organisation/compliance/csp-exception-request/link',
      // listAll: all standalone org exceptions (multiselect options + view-all page)
      listAll: '/organisation/compliance/csp-exception-request/list',
      // linked: exception ids linked to a control (?controlId=)
      linked: (organisationStandardId: string) =>
        `/organisation/compliance/csp-exception-request/linked/${organisationStandardId}`,
      update: (id: string) => `/organisation/compliance/csp-exception-request/${id}`,
    },

    cspAssessmentDetailRisk: {
      base: '/organisation/compliance/csp-assessment-detail-risk',
    },

    cspReportingCalendar: {
      base: '/organisation/compliance/csp-reporting-calendar',
      // submit: `${base}/:organisationStandardId/submit`
      submit: (organisationStandardId: string) =>
        `/organisation/compliance/csp-reporting-calendar/${organisationStandardId}/submit`,
    },

    fiscalYear: {
      list: '/organisation/compliance/fiscal-year/list',
    },

    cspAgencySetup: {
      base: '/organisation/compliance/csp-agency-setup',
      getByOrganisationStandardId: (organisationStandardId: string) =>
        `/organisation/compliance/csp-agency-setup/${organisationStandardId}`,
      previousByOrganisationStandardId: (organisationStandardId: string) =>
        `/organisation/compliance/csp-agency-setup/previous/${organisationStandardId}`,
    },

    cspDashboard: {
      base: '/organisation/compliance/csp-dashboard',
    },

    cspNonComplianceAcknowledgement: {
      base: '/organisation/compliance/csp-non-compliance-acknowledgement',
      assessmentDetailBase: '/organisation/compliance/csp-non-compliance-acknowledgement/assessment-detail',
      listByOrganisationStandardId: (organisationStandardId: string) =>
        `/organisation/compliance/csp-non-compliance-acknowledgement/${organisationStandardId}`,
      deleteByAssessmentDetailId: (assessmentDetailId: string) =>
        `/organisation/compliance/csp-non-compliance-acknowledgement/assessment-detail/${assessmentDetailId}`,
    },

    task: {
      taskSeverity: '/organisation/task/task-severity',
      taskSeverityList: '/organisation/task/task-severity/list',
      taskStatus: '/organisation/task/task-status',
      changeTaskStatusSequence: '/organisation/task/task-status/sequence',
      taskStatusList: '/organisation/task/task-status/list',
      task: '/organisation/task',
      countByProject: '/organisation/task/count',
      recurringByParent: '/organisation/task/recurring',
      createRecurringTask: '/organisation/task/create-recurring',
      cancelRecurringParent: '/organisation/task/cancel-recurring',
      generateDescription: '/organisation/task/ai-generate-description',
      pciTaskAiAssessment: '/organisation/task/ai/pci-assessment',
      comment: '/organisation/task/comment',
      allDetails: '/organisation/task/all-details',
      changeTaskStatus: '/organisation/task/status',
      pciTaskStatus: '/organisation/task/pci-status',
      updatePCIAssessment: '/organisation/task/pci-assessment',
      tasksForTimeline: '/organisation/task/timeline',
      tasksForCalendar: '/organisation/task/calendar',
      tasksForCalendarSummary: '/organisation/task/calendar/summary',
      linkFileToPciAssessmentDetail: '/organisation/task/pci-assessment-detail/link-file',
      unlinkFileFromPciAssessmentDetail: '/organisation/task/pci-assessment-detail/unlink-file',
      linkFileToTask: '/organisation/task/files/link',
      unlinkFileFromTask: '/organisation/task/files/unlink',
      refreshPciTaskSuggestion: '/organisation/task/ai/pci-suggestion',
      retryPciEvidenceIngest: '/organisation/task/ai/pci-evidence/retry-ingest',
    },

    serviceProvider: {
      serviceProvider: '/organisation/service-provider',
      generateServiceProviderDescription: '/organisation/service-provider/ai/generate-description',
      generateServiceProviderDescriptionStream: '/organisation/service-provider/ai/generate-description/stream',
      generateServiceProvider: '/organisation/service-provider/ai/generate-service-provider',
      generateServiceProviderStream: '/organisation/service-provider/ai/generate-service-provider/stream',
      vendorUser: '/organisation/service-provider/vendor-user',
      uploadExcel: '/organisation/service-provider/upload',
      logo: '/organisation/service-provider/logo',
      serviceProviderContractStatus: '/organisation/service-provider/service-provider-contract-status',
      assessmentTierMapping: '/organisation/service-provider/assessment-tier-mapping',
      serviceType: '/organisation/service-provider/service-type',
      assessmentTierSensitivity: '/organisation/service-provider/assessment-tier-sensitivity',
      assessmentTierCriticality: '/organisation/service-provider/assessment-tier-criticality',
      assessmentTier: '/organisation/service-provider/assessment-tier',
      deriveTier: '/organisation/service-provider/derive-tier',
      riskTierMatrix: '/organisation/service-provider/risk-tier-matrix',
      form: {
        generalDetails: '/organisation/service-provider/form/general-details',
        assuranceProfileDetails: '/organisation/service-provider/form/assurance-profile-details',
      },
      report: {
        report: '/organisation/service-provider/report',
        serviceType: '/organisation/service-provider/report/service-type',
        contractStatus: '/organisation/service-provider/report/contract-status',
        riskRating: '/organisation/service-provider/report/risk-rating',
        assessmentTier: '/organisation/service-provider/report/assessment-tier',
      },
      assessmentTemplate: '/organisation/service-provider/assessment-template',
      assessmentTemplateUpload: '/organisation/service-provider/assessment-template/upload',
      assessmentTemplateQuestionnaire: '/organisation/service-provider/assessment-template-questionnaire',
      assessmentTemplateQuestionnaireQuestion:
        '/organisation/service-provider/assessment-template-questionnaire-question',
      assessmentTemplateBulkCreate: '/organisation/service-provider/assessment-template-questionnaire/bulk-create',
      assessmentTemplateQuestionnaireAiGenerate:
        '/organisation/service-provider/assessment-template-questionnaire/ai-generate',
      assessment: '/organisation/service-provider/assessment',
      updateAssessmentDueDate: (id: string) => `/organisation/service-provider/assessment/${id}/due-date`,
      sendReminderEmail: '/organisation/service-provider/assessment/send-reminder-email',
      approveRejectResponse: '/organisation/service-provider/assessment/response/approve-reject',
      approveRejectMultipleResponses: '/organisation/service-provider/assessment/response/approve-reject-multiple',
      assessmentReport: {
        generate: (id: string) => `/organisation/service-provider/assessment/${id}/report/generate`,
        status: (id: string) => `/organisation/service-provider/assessment/${id}/report/status`,
        download: (reportId: string) => `/organisation/service-provider/assessment/report/${reportId}/download`,
        // Report Library - list base; delete appends `/:reportId`, cancel appends `/:reportId/cancel-generation`
        reports: '/organisation/service-provider/assessment/reports',
        report: '/organisation/service-provider/assessment/report',
      },
      assessmentForm: {
        selectServiceProvider: '/organisation/service-provider/assessment/form/select-service-provider',
        selectAssessmentTemplate: '/organisation/service-provider/assessment/form/select-assessment-template',
        dueDateAndReminders: '/organisation/service-provider/assessment/form/due-date-and-reminders',
        emailCustomization: '/organisation/service-provider/assessment/form/email-customization',
        reviewAndSubmit: '/organisation/service-provider/assessment/form/review-and-submit',
      },
      incomingQuestionnaire: {
        base: '/organisation/service-provider/incoming-questionnaire',
        parseFile: (id: string) => `/organisation/service-provider/incoming-questionnaire/${id}/parse-file`,
        questions: (id: string) => `/organisation/service-provider/incoming-questionnaire/${id}/questions`,
        reorderQuestions: (id: string) =>
          `/organisation/service-provider/incoming-questionnaire/${id}/questions/reorder`,
        updateQuestion: (id: string, questionId: string) =>
          `/organisation/service-provider/incoming-questionnaire/${id}/questions/${questionId}`,
        deleteQuestion: (id: string, questionId: string) =>
          `/organisation/service-provider/incoming-questionnaire/${id}/questions/${questionId}`,
        assignQuestion: (id: string, questionId: string) =>
          `/organisation/service-provider/incoming-questionnaire/${id}/questions/${questionId}/assign`,
        assignAllQuestions: (id: string) => `/organisation/service-provider/incoming-questionnaire/${id}/assign-all`,
        unassigned: (id: string) => `/organisation/service-provider/incoming-questionnaire/${id}/unassigned`,
        transition: (id: string) => `/organisation/service-provider/incoming-questionnaire/${id}/transition`,
        questionResponse: (questionId: string) =>
          `/organisation/service-provider/incoming-questionnaire/questions/${questionId}/response`,
        reviewResponse: (responseId: string) =>
          `/organisation/service-provider/incoming-questionnaire/responses/${responseId}/review`,
        evidence: '/organisation/service-provider/incoming-questionnaire/evidence',
        evidenceLink: '/organisation/service-provider/incoming-questionnaire/evidence/link',
        evidenceDownload: (evidenceId: string) =>
          `/organisation/service-provider/incoming-questionnaire/evidence/${evidenceId}/download`,
        complete: (id: string) => `/organisation/service-provider/incoming-questionnaire/${id}/complete`,
        export: (id: string) => `/organisation/service-provider/incoming-questionnaire/${id}/export`,
        submit: (id: string) => `/organisation/service-provider/incoming-questionnaire/${id}/submit`,
        aiDrafts: (id: string) => `/organisation/service-provider/incoming-questionnaire/${id}/ai-drafts`,
        retryAiDraft: (id: string, jobId: string) =>
          `/organisation/service-provider/incoming-questionnaire/${id}/ai-drafts/jobs/${jobId}/retry`,
        acceptAiDraft: (id: string, suggestionId: string) =>
          `/organisation/service-provider/incoming-questionnaire/${id}/ai-drafts/suggestions/${suggestionId}/accept`,
        editAiDraft: (id: string, suggestionId: string) =>
          `/organisation/service-provider/incoming-questionnaire/${id}/ai-drafts/suggestions/${suggestionId}/edit`,
      },
      aiRisksByAssessment: (assessmentId: string) =>
        `/organisation/service-provider/assessment/${assessmentId}/ai-risks`,
      aiRisksGenerate: (assessmentId: string) =>
        `/organisation/service-provider/assessment/${assessmentId}/ai-risks/generate`,
      aiRisk: '/organisation/service-provider/ai-risk',
      aiRiskGet: (id: string) => `/organisation/service-provider/ai-risk/${id}`,
      aiRiskUpdate: (id: string) => `/organisation/service-provider/ai-risk/${id}`,
      aiRiskApprove: (id: string) => `/organisation/service-provider/ai-risk/${id}/approve`,
      aiRiskReject: (id: string) => `/organisation/service-provider/ai-risk/${id}/reject`,
      riskReview: (id: string) => `/organisation/service-provider/${id}/risk-review`,
      riskReviews: (id: string) => `/organisation/service-provider/${id}/risk-reviews`,
      riskPosture: (id: string) => `/organisation/service-provider/${id}/risk-posture`,
      riskReviewIntervals: `/organisation/service-provider/risk-review-intervals`,
      sia: {
        base: '/organisation/service-provider',
        resource: '/organisation/service-provider/sia',
        forProvider: (spId: string) => `/organisation/service-provider/${spId}/sia`,
        byId: (siaId: string) => `/organisation/service-provider/sia/${siaId}`,
        submit: (siaId: string) => `/organisation/service-provider/sia/${siaId}/submit`,
        approve: (siaId: string) => `/organisation/service-provider/sia/${siaId}/approve`,
        returnForRework: (siaId: string) => `/organisation/service-provider/sia/${siaId}/return`,
        clone: (siaId: string) => `/organisation/service-provider/sia/${siaId}/clone`,
        processDependency: (siaId: string) => `/organisation/service-provider/sia/${siaId}/process-dependency`,
        processDependencyById: (siaId: string, depId: string) =>
          `/organisation/service-provider/sia/${siaId}/process-dependency/${depId}`,
        impact: (siaId: string, dimension: string) => `/organisation/service-provider/sia/${siaId}/impact/${dimension}`,
      },

      evaluation: {
        evaluate: (assessmentId: string) => `/organisation/service-provider/assessment/${assessmentId}/evaluate`,
        listForAssessment: (assessmentId: string) =>
          `/organisation/service-provider/evaluation/assessment/${assessmentId}`,
        compare: '/organisation/service-provider/evaluation/compare',
        job: (jobId: string) => `/organisation/service-provider/evaluation/${jobId}`,
        parsedResponses: (jobId: string) => `/organisation/service-provider/evaluation/${jobId}/parsed-responses`,
        parsedResponse: (jobId: string, id: string) =>
          `/organisation/service-provider/evaluation/${jobId}/parsed-responses/${id}`,
        scores: (jobId: string) => `/organisation/service-provider/evaluation/${jobId}/scores`,
        recalculateScores: (jobId: string) => `/organisation/service-provider/evaluation/${jobId}/scores/recalculate`,
        alerts: (jobId: string) => `/organisation/service-provider/evaluation/${jobId}/alerts`,
        acknowledgeAlert: (jobId: string, alertId: string) =>
          `/organisation/service-provider/evaluation/${jobId}/alerts/${alertId}/acknowledge`,
        summary: (jobId: string) => `/organisation/service-provider/evaluation/${jobId}/summary`,
        criticalGaps: (jobId: string) => `/organisation/service-provider/evaluation/${jobId}/summary/critical-gaps`,
        export: (jobId: string) => `/organisation/service-provider/evaluation/${jobId}/export`,
        auditTrail: (jobId: string) => `/organisation/service-provider/evaluation/${jobId}/audit-trail`,
      },

      controlMapping: {
        baseline: '/organisation/service-provider/control-mapping/baseline',
        overrides: '/organisation/service-provider/control-mapping/overrides',
        override: (id: string) => `/organisation/service-provider/control-mapping/overrides/${id}`,
        copyFromBaseline: '/organisation/service-provider/control-mapping/overrides/copy-from-baseline',
        mappedControls: '/organisation/service-provider/control-mapping/mapped-controls',
        preview: '/organisation/service-provider/control-mapping/preview',
      },

      scoringRubric: {
        active: '/organisation/service-provider/scoring-rubric',
        list: '/organisation/service-provider/scoring-rubric/list',
        thresholds: '/organisation/service-provider/scoring-rubric/thresholds',
      },
    },

    risk: {
      risk: '/organisation/risk',
      ratingFromLikelihoodAndImpact: '/organisation/risk/rating-from-likelihood-impact',
      planOfAction: '/organisation/risk/plan-of-action',
      aiGenerateRisk: '/organisation/risk/ai/generate-risk',
      aiGenerateRiskStream: '/organisation/risk/ai/generate-risk/stream',
      uploadExcel: '/organisation/risk/upload',
      riskCategories: '/organisation/risk/risk-categories',
      vulnerability: '/organisation/risk/vulnerability',
      generateVulnerability: '/organisation/risk/vulnerability/ai/generate',
      generateVulnerabilityStream: '/organisation/risk/vulnerability/ai/generate/stream',
      runZeroVulnerability: '/organisation/risk/vulnerability/run-zero',
      runZeroVulnerabilityDeleteAll: '/organisation/risk/vulnerability/run-zero/all',
      runZeroVulnerabilityReport: '/organisation/risk/vulnerability/run-zero/report',
      moveRunZeroVulnerabilityToVulnerability: '/organisation/risk/vulnerability/run-zero/move-to-vulnerability',
      moveRunZeroVulnerabilitiesToVulnerabilities: '/organisation/risk/vulnerability/run-zero/move-to-vulnerabilities',
      threatCategory: '/organisation/risk/threat-categories',
      threatSource: '/organisation/risk/threat-sources',
      threatActor: '/organisation/risk/threat-actors',
      threat: '/organisation/risk/threats',
      riskRatings: '/organisation/risk/risk-ratings',
      riskOwners: '/organisation/risk/risk-owners',
      riskOwnersAssociatedWithRisks: '/organisation/risk/risk-owners/associated-with-risks',
      riskOwnerMigration: '/organisation/risk/risk-owners/migrate',
      riskImpacts: '/organisation/risk/risk-impacts',
      riskIdentificationSources: '/organisation/risk/risk-identification-sources',
      riskLikelihood: '/organisation/risk/risk-likelihood',
      riskMatrix: '/organisation/risk/risk-matrix',
      riskMatrixRecalculate: '/organisation/risk/risk-matrix/recalculate',
      riskStats: '/organisation/risk/risk-stats',
      riskHeatMap: '/organisation/risk/risk-heatmap',
      riskMedianScore: '/organisation/risk/risk-score',
      riskByCategory: '/organisation/risk/risk-by-category',
      riskAssets: '/organisation/risk/risk-assets',
      riskTracking: '/organisation/risk/risk-tracking',
      riskList: '/organisation/risk/list',
      incident: '/organisation/risk/incident',
      includeExcelUpload: '/organisation/risk/incident/upload',
      incidentTypes: '/organisation/risk/incident-types',
      incidentForm: {
        coreDetails: '/organisation/risk/incident',
        investigationAndResponse: '/organisation/risk/incident/investigation-and-response',
        postIncidentAnalysis: '/organisation/risk/incident/post-incident-analysis',
        nessus: '/organisation/risk/nessus',
        ai: {
          generateIncidentDescription: '/organisation/risk/incident/ai/generate-description',
          generateIncidentRootCause: '/organisation/risk/incident/ai/generate-root-cause',
          generateIncidentMitigationSteps: '/organisation/risk/incident/ai/generate-mitigation-steps',
          generateLessonsLearned: '/organisation/risk/incident/ai/generate-lessons-learned',
          generateIncident: '/organisation/risk/incident/ai/generate-incident',
          generateIncidentStream: '/organisation/risk/incident/ai/generate-incident/stream',
        },
      },
      incidentReport: {
        report: '/organisation/risk/incident/report',
        type: '/organisation/risk/incident/report/type',
        status: '/organisation/risk/incident/report/status',
        severity: '/organisation/risk/incident/report/severity',
        impact: '/organisation/risk/incident/report/impact',
        byMonth: '/organisation/risk/incident/report/by-month',
      },

      report: {
        inherentRiskRating: '/organisation/risk/report/inherent-risk-rating',
        residualRiskRating: '/organisation/risk/report/residual-risk-rating',
      },

      form: {
        generalInfo: '/organisation/risk/form/general-info',
        inherentLikelihoodAndImpact: '/organisation/risk/form/inherent-likelihood-and-impact',
        control: '/organisation/risk/form/control',
        remediationControl: '/organisation/risk/form/remediation-control',
        residualRisk: '/organisation/risk/form/residual-risk',
        remediation: '/organisation/risk/form/remediation',
      },
      ai: {
        generateLikelihoodAndImpact: '/organisation/risk/ai/generate-likelihood-and-impact',
        generateResidualRiskLikelihoodAndImpact: '/organisation/risk/ai/generate-residual-risk-likelihood-and-impact',
        generateRemediation: '/organisation/risk/ai/generate-remediation',
        generateMitigationControls: '/organisation/risk/ai/generate-mitigation-controls',
      },
      duplicate: '/organisation/risk/duplicate',
      createProject: '/organisation/risk/create-project',
      appetite: {
        base: '/organisation/risk/appetite',
        submit: '/organisation/risk/appetite/submit',
        endorse: '/organisation/risk/appetite/endorse',
      },
      escalations: {
        base: '/organisation/risk/escalations',
      },
      highExtremeResidualRiskReport: {
        base: '/organisation/risk/high-extreme-residual-risk-report',
        generate: '/organisation/risk/high-extreme-residual-risk-report/generate',
      },
      treatmentDecision: '/organisation/risk/treatment-decision',
    },
    utils: {
      rephraseContent: '/organisation/utils/ai/rephrase-content',
    },

    policy: {
      policy: '/organisation/policy',
      acknowledgementReport: '/organisation/policy/acknowledgement-report',
      acknowledgementHistory: '/organisation/policy/acknowledgement-history',
      pendingAcknowledgements: '/organisation/policy/acknowledgement/pending',
      pendingAcknowledgementCount: '/organisation/policy/acknowledgement/pending/count',
      uploadExcel: '/organisation/policy/upload',
      uploadMarkdownPreview: '/organisation/policy/upload-markdown/preview',
      uploadMarkdownCommit: '/organisation/policy/upload-markdown/commit',
      policyReviewFrequencies: '/organisation/policy/policy-review-frequencies',
      policyCategories: '/organisation/policy/policy-categories',
      policyBundles: '/organisation/policy/bundles',
      policyBundleRuns: '/organisation/policy/bundles/runs',
      policyBundleInferMetadata: '/organisation/policy/bundles/infer-metadata',
      policyBundlePrompts: '/organisation/policy/bundles/prompts',
      policyReport: '/organisation/policy/report',
      policyReportCategory: '/organisation/policy/report/category',
      policyReportStatus: '/organisation/policy/report/status',
      policyReportHealth: '/organisation/policy/report/health',
      policyReportOwner: '/organisation/policy/report/owner',
      policyReportRecentlyUpdated: '/organisation/policy/report/recently-updated',
      policyDashboardReport: '/organisation/policy/report',
      policyDashboardBenchmarks: '/organisation/policy/report/dashboard-benchmarks',
      generatePolicy: '/organisation/policy/ai/generate-policy',
      generatePolicyStream: '/organisation/policy/ai/generate-policy/stream',
      editPolicySectionsStream: '/organisation/policy/ai/edit-sections/stream',
      recordPolicyAiEditApplication: '/organisation/policy/ai/edit-sections/applied',
      summaryStream: '/organisation/policy/ai/summary',
      sendReminderEmail: '/organisation/policy/send-reminder-email',
      assignPolicyToStaffForAcknowledgement: '/organisation/policy/assign-policy-to-staff-for-acknowledgement',
      resendAcknowledgementEmail: '/organisation/policy/resend-acknowledgement-email',
      resendAcknowledgementEmailsForPolicy: '/organisation/policy/resend-acknowledgement-emails-for-policy',
      sharePolicies: '/organisation/policy/share-policies',
      sendReminderEmailForReviewAndApproval: '/organisation/policy/send-reminder-email-for-review-and-approval',
      history: '/organisation/policy/history',
      reviewReport: '/organisation/policy/review-report',
      reviewHistory: '/organisation/policy/review-history',
    },

    // DIGITAL SIGNATURE
    signature: {
      profile: '/organisation/signature/profile',
      settings: '/organisation/signature/settings',
      keys: '/organisation/signature/keys',
      rotateKey: '/organisation/signature/keys/rotate',
      chainVerify: '/organisation/signature/chain/verify',
      revoke: '/organisation/signature/revoke',
      certificate: '/organisation/signature',
    },

    // DOCUMENT REPOSITORY
    documentRepo: {
      fileLinks: '/organisation/file-repository/file-links',
      fileShortcuts: '/organisation/file-repository/file-shortcuts',
      fileLinkShortcuts: '/organisation/file-repository/file-link-shortcuts',
      fileLinksMove: '/organisation/file-repository/file-links/move',
      getFolderIdFromFolderName: '/organisation/file-repository/repository-and-folder-ids',
      getBreadcrumb: '/organisation/file-repository/get-breadcrumb',
      getRepositoryNameById: '/organisation/file-repository/get-repository-name',
      repositories: '/organisation/file-repository/repositories',
      protectRepository: '/organisation/file-repository/repositories/protect',
      sharePoint: '/organisation/file-repository/share-to-sharepoint',
      sharePointSync: '/organisation/file-repository/sync-sharepoint',
      googleDriveSync: '/organisation/file-repository/sync-google-drive',
      folders: '/organisation/file-repository/folders',
      files: '/organisation/file-repository/files',
      filesList: '/organisation/file-repository/files/list',
      filesUpload: '/organisation/file-repository/files/upload',
      filesCopy: '/organisation/file-repository/files/copy',
      filesMove: '/organisation/file-repository/files/move',
      filesRename: '/organisation/file-repository/files/rename',
      restoreFileVersion: '/organisation/file-repository/files/restore-version',
      downloadRepositoryAsZip: '/organisation/file-repository/repositories/download-zip',
      downloadFolderAsZip: '/organisation/file-repository/folders/download-zip',
      commentToFile: '/organisation/file-repository/files/comment',
      getQuarantinedFiles: '/organisation/file-repository/get-quarantined-files',
      pciRepositoryAndFolderIds: '/organisation/file-repository/pci-repository-and-folder-ids',
    },

    // Dashboard Reports
    reports: {
      comprehensive: '/organisation/reports/comprehensive-dashboard',
    },

    countryRegulatoryRegister: {
      countryRegulatoryRegister: '/organisation/country-regulatory-register-oos',
      generate: '/organisation/country-regulatory-register-oos/ai/generate',
      generateStream: '/organisation/country-regulatory-register-oos/ai/generate/stream',
      jurisdiction: '/organisation/country-regulatory-register-oos/jurisdiction',
      operationType: '/organisation/country-regulatory-register-oos/operation-type',
      regulatoryCategory: '/organisation/country-regulatory-register-oos/regulatory-category',
      region: '/organisation/country-regulatory-register-oos/region',
      riskType: '/organisation/country-regulatory-register-oos/risk-type',
      uploadExcel: '/organisation/country-regulatory-register-oos/upload',
    },

    exemptionsRegister: {
      exemptionsRegister: '/organisation/exemptions-register',
      uploadExcel: '/organisation/exemptions-register/upload',
      reviewOrApproveExemptionRegister: '/organisation/exemptions-register/review-or-approve',
      sendReminderEmail: '/organisation/exemptions-register/send-reminder-email',
      exemptionsEvidence: '/organisation/exemptions-evidence',
      generateExemption: '/organisation/exemptions-register/ai/generate-exemption',
      generateExemptionStream: '/organisation/exemptions-register/ai/generate-exemption/stream',
    },

    organisationalContext: {
      organisationalContext: '/organisation/organisational-context',
      updateStatus: '/organisation/organisational-context/status',
      parseAssistant: '/organisation/organisational-context/assistant/parse',
      messages: '/organisation/organisational-context/assistant/messages',
    },

    notification: {
      getAll: '/organisation/notification',
      counts: '/organisation/notification/counts',
      markAsRead: '/organisation/notification/mark-as-read',
      markAllAsRead: '/organisation/notification/mark-all-as-read',
    },

    riskAssessment: {
      systemExposure: '/organisation/risk-assessment/system-exposure',
      hostingModel: '/organisation/risk-assessment/hosting-model',
      dataClassification: '/organisation/risk-assessment/data-classification',
      userBase: '/organisation/risk-assessment/user-base',
      assessmentType: '/organisation/risk-assessment/assessment-type',
      assessment: '/organisation/risk-assessment/assessment',
      generateRisk: '/organisation/risk-assessment/assessment/generate-risk',
      generateRiskStream: '/organisation/risk-assessment/assessment/generate-risk/stream',
    },
    peopleRiskAssessment: {
      assessmentType: '/organisation/people-risk-assessment/assessment-type',
      assessment: '/organisation/people-risk-assessment/assessment',
      generateRisk: '/organisation/people-risk-assessment/assessment/generate-risk',
      generateRiskStream: '/organisation/people-risk-assessment/assessment/generate-risk/stream',
    },

    /** CISO Adapt backend - Phished integration (proxies https://api.phished.io) */
    phishedIntegration: {
      testConnection: '/organisation/phished/test-connection',
      dashboard: '/organisation/phished/dashboard',
      evidence: '/organisation/phished/evidence',
      proxy: '/organisation/phished/proxy',
      recipients: '/organisation/phished/recipients',
      recipientsWithTags: '/organisation/phished/recipients-with-tags',
      reconcileRecipients: '/organisation/phished/reconcile-recipients',
      syncRecipients: '/organisation/phished/sync-recipients',
      recipientById: (id: string) => `/organisation/phished/recipients/${id}`,
      recipientSummary: (id: string) => `/organisation/phished/recipients/${id}/summary`,
      recipientBrs: (id: string) => `/organisation/phished/recipients/${id}/brs`,
      recipientBrsHistory: (id: string) => `/organisation/phished/recipients/${id}/brs/history`,
      recipientSimulationEvents: (id: string) => `/organisation/phished/recipients/${id}/simulation-events`,
      recipientAcademyStatistics: (id: string) => `/organisation/phished/recipients/${id}/academy/statistics`,

      recipientAcademyProgression: (id: string) => `/organisation/phished/recipients/${id}/academy/progression`,
      recipientExpiredSessions: (id: string) => `/organisation/phished/recipients/${id}/academy/expired-sessions`,

      simulationSettings: '/organisation/phished/simulations/settings',
      simulationEvents: '/organisation/phished/simulations/events',
      simulationManualTemplates: '/organisation/phished/simulations/manual/templates',
      simulationScheduled: '/organisation/phished/simulations/scheduled',
      simulationButtonResponses: '/organisation/phished/simulations/button-responses',
      simulationLandingPages: '/organisation/phished/simulations/landing-pages',

      simulationManualSendsByDate: (date: string) => `/organisation/phished/simulations/sends/manual/date/${date}`,
      simulationAutomatedSendsByDate: (date: string) =>
        `/organisation/phished/simulations/sends/automated/date/${date}`,

      academyLevelProgression: '/organisation/phished/reports/academy/level-progression',
      academyTrainingProgression: '/organisation/phished/reports/academy/training-progression',
      myAppsAndDevices: '/organisation/phished/reports/academy/recipients/my-apps-and-devices',
      threatAlerts: '/organisation/phished/reports/academy/recipients/threat-alerts',

      departments: '/organisation/phished/departments',
      locations: '/organisation/phished/locations',
      domains: '/organisation/phished/domains',
      smtp: '/organisation/phished/smtp',
      ssoConfigurations: '/organisation/phished/sso-configurations',
      academySettings: '/organisation/phished/academy/settings',
      administrators: '/organisation/phished/administrators',
      replyTemplates: '/organisation/phished/reply-templates',
      activationReports: '/organisation/phished/activation-reports',
      academyModules: '/organisation/phished/academy/modules',
      academyTargetLists: '/organisation/phished/academy/target-lists',

      riskCategoriesReport: '/organisation/phished/reports/risk-categories',
    },
    aiRegister: {
      aiRegister: '/organisation/ai-register',
      uploadExcel: '/organisation/ai-register/upload',
      generate: '/organisation/ai-register/ai/generate',
      generateStream: '/organisation/ai-register/ai/generate/stream',
      type: '/organisation/ai-register/type',
      permittedData: '/organisation/ai-register/permitted-data',
      screeningQuestionnaire: '/organisation/ai-register/screening-questionnaire',
      screeningQuestionnaireUploadExcel: '/organisation/ai-register/screening-questionnaire/upload',
      screening: '/organisation/ai-register/screening',
      report: {
        type: '/organisation/ai-register/report/type',
        permittedData: '/organisation/ai-register/report/permitted-data',
        status: '/organisation/ai-register/report/status',
        count: '/organisation/ai-register/report/count',
      },
      generateRisk: '/organisation/ai-register/generate-risk',
    },

    ctra: {
      programs: '/organisation/ctra/programs',
      programDiagram: (id: string) => `/organisation/ctra/programs/${id}/diagram`,
      programDiagramChildren: (id: string) => `/organisation/ctra/programs/${id}/diagram/children`,
      zones: '/organisation/ctra/zones',
      engagements: '/organisation/ctra/engagements',
      engagementDiagram: (id: string) => `/organisation/ctra/engagements/${id}/diagram`,
      engagementHistory: '/organisation/ctra/engagements/history',
      engagementStep: {
        basicInfo: '/organisation/ctra/engagements/basic-info',
        assets: '/organisation/ctra/engagements/assets',
        dataFlows: '/organisation/ctra/engagements/data-flows',
      },
      assetsList: '/organisation/ctra/engagements/assets-list',
      soa: '/organisation/ctra/soa',
      soaGenerate: '/organisation/ctra/soa/generate',
      soaCancelGeneration: '/organisation/ctra/soa/cancel',
      questionnaireBatch: '/organisation/ctra/questionnaire-batch',
      questionnaireBatchQuestion: '/organisation/ctra/questionnaire-batch/question',
      questionnaireBatchRecipient: '/organisation/ctra/questionnaire-batch-recipient',
      questionnaireBatchRecipientSend: '/organisation/ctra/questionnaire-batch-recipient/send',
      questionnaireBatchRecipientResponses: (id: string) =>
        `/organisation/ctra/questionnaire-batch-recipient/${id}/responses`,
      questionnaireResponse: '/organisation/ctra/questionnaire-response',
      safeguard: '/organisation/ctra/safeguard',
      vulnerability: '/organisation/ctra/vulnerability',
      threatAgent: '/organisation/ctra/threat-agent',
      threatAgentGenerate: '/organisation/ctra/threat-agent/generate',
      threatScenario: '/organisation/ctra/threat-scenario',
      threatScenarioGenerate: '/organisation/ctra/threat-scenario/generate',
      threatScenarioControlHierarchy: '/organisation/ctra/threat-scenario/control-hierarchy',
      threatScenarioCatalog: '/organisation/ctra/threat-scenario-catalog',
      risk: '/organisation/ctra/risk',
      riskGenerate: '/organisation/ctra/risk/generate',
      riskCalculatedLikelihood: '/organisation/ctra/risk/calculated-likelihood',
      riskPromote: '/organisation/ctra/risk/promote',
      recommendation: '/organisation/ctra/recommendation',
      report: '/organisation/ctra/report',
      reportGenerate: '/organisation/ctra/report/generate',
    },

    informationSecurityObjectives: {
      objectives: '/organisation/information-security-objectives',
      status: '/organisation/information-security-objectives/status',
      priority: '/organisation/information-security-objectives/priority',
      reportingFormat: '/organisation/information-security-objectives/reporting-format',
      analysisEvaluationFrequency: '/organisation/information-security-objectives/analysis-evaluation-frequency',
      monitoringFrequency: '/organisation/information-security-objectives/monitoring-frequency',
      measureType: '/organisation/information-security-objectives/measure-type',
      area: '/organisation/information-security-objectives/area',
      form: {
        generalInfo: '/organisation/information-security-objectives/form/general-info',
        measurement: '/organisation/information-security-objectives/form/measurement',
        accountability: '/organisation/information-security-objectives/form/accountability',
        review: '/organisation/information-security-objectives/form/review',
      },
      uploadExcel: '/organisation/information-security-objectives/upload',
    },

    ismsAssuranceTask: {
      task: '/organisation/isms-assurance-task',
      uploadExcel: '/organisation/isms-assurance-task/upload',
      convertToProject: '/organisation/isms-assurance-task/convert-to-project',
    },
  },

  // SUPER ADMIN
  admin: {
    dashboards: '/admin/dashboards',
    admin: '/admin/admin',
    phishedRecipientTagColor: '/admin/phished-recipient-tag-color',
    assessment: '/admin/compliance/assessment',
    database: {
      access: {
        requestOtp: '/admin/database/access/request-otp',
        verifyOtp: '/admin/database/access/verify-otp',
        accessStatus: '/admin/database/access/status',
      },
      ai: {
        tables: '/admin/database/ai/tables',
        tableRows: (table: string) => `/admin/database/ai/tables/${encodeURIComponent(table)}/rows`,
      },
      primary: {
        tables: '/admin/database/primary/tables',
        tableRows: (table: string) => `/admin/database/primary/tables/${encodeURIComponent(table)}/rows`,
      },
    },
    commonDocumentPdfTemplate: {
      base: '/admin/common-document-pdf-template',
      byType: (type: string) => `/admin/common-document-pdf-template/${type}`,
      logo: (type: string) => `/admin/common-document-pdf-template/${type}/logo`,
    },
    assessmentAuditInformation: '/admin/compliance/assessment/audit-information',
    resetAdminPassword: '/admin/admin/reset-password',
    signupRequest: '/admin/signup-request',
    organisation: '/admin/organisation',
    organisationFieldRequirements: (id: string) => `/admin/organisation/${id}/field-requirements`,
    unlinkParentOrganisation: '/admin/organisation/unlink-parent-organisation',
    organisationAllowedStandards: '/admin/organisation-allowed-standards',
    enterpriseConnections: '/admin/enterprise-connections',
    emailTemplate: '/admin/email-template',
    mailLogs: '/admin/mail-logs',
    organisationPasswordPolicy: (id: string) => `/admin/organisation/${id}/password-policy`,
    organisationDataExport: (id: string) => `/admin/organisation/${id}/data-export`,
    organisationDataExportPreview: (id: string) => `/admin/organisation/${id}/data-export/preview`,
    organisationDataExportStatus: (id: string) => `/admin/organisation/${id}/data-export/status`,
    organisationDataExportRequestOtp: (id: string) => `/admin/organisation/${id}/data-export/request-otp`,
    organisationDataExportVerifyOtp: (id: string) => `/admin/organisation/${id}/data-export/verify-otp`,
    helpfulTips: '/admin/helpful-tips',
    userGuides: '/admin/user-guides',
    userGuideApiKeys: '/admin/user-guide-api-keys',
    releaseNotes: '/admin/release-notes',
    apiDocsTokens: '/admin/api-docs-tokens',
    firebaseNotification: {
      getAllUsersWithFcmToken: '/admin/firebase-notification/users-with-fcm-token',
      sendNotificationToUser: '/admin/firebase-notification/send-notification-to-user',
      sendNotificationToAllUsersInOrganisation:
        '/admin/firebase-notification/send-notification-to-all-users-in-organisation',
    },
    organisationReportTemplate: '/admin/organisation/report-template',
    user: '/admin/user',
    resetUserPassword: '/admin/user/reset-password',
    organisationUsers: '/admin/organisation-users',
    clerkUsers: '/admin/user/clerk/list',
    clerkUser: '/admin/user/clerk',
    resource: '/admin/resources',
    module: '/admin/module',
    pushBugReportToAzureBoard: '/bug-report/push-to-azure-board',
    aiModels: '/admin/ai-models',
    aiModelsDiscoverModels: '/admin/ai-models/discover-models',
    aiPromptCatalog: {
      list: '/admin/ai-prompt-catalog',
      summary: '/admin/ai-prompt-catalog/summary',
      markdown: '/admin/ai-prompt-catalog/markdown',
      refresh: '/admin/ai-prompt-catalog/refresh',
    },
    aiToken: {
      aiTokenReset: '/admin/ai-tokens/reset',
      aiTokenUsageHistory: '/admin/ai-tokens/usage-history',
    },
    assistant: {
      conversations: '/admin/assistant/conversations',
      conversationById: (id: string) => `/admin/assistant/conversations/${id}`,
    },
    aiRag: {
      controlEmbeddingFrameworks: '/admin/ai-rag/control-embeddings/frameworks',
      controlEmbeddingList: '/admin/ai-rag/control-embeddings/list',
      syncControlEmbeddings: '/admin/ai-rag/control-embeddings/sync',
      deleteControlEmbeddings: '/admin/ai-rag/control-embeddings',
      evidenceDocuments: '/admin/ai-rag/documents',
      evidenceDocumentById: (id: string) => `/admin/ai-rag/documents/${id}`,
      controlMappingSuggest: '/admin/ai-rag/control-mappings/suggest',
      controlMappingReview: '/admin/ai-rag/control-mappings/review',
      controlMappings: '/admin/ai-rag/control-mappings',
      policyTemplates: '/admin/ai-policy-templates',
      policyTemplateValidate: '/admin/ai-policy-templates/validate',
      policyTemplateRegenerate: (id: string) => `/admin/ai-policy-templates/${id}/regenerate`,
      policyPacks: '/admin/ai-policy-packs',
    },
    agents: {
      enablements: '/admin/agent-enablements',
      enablement: (organisationId: string, agentName: string) =>
        `/admin/agent-enablements/${organisationId}/${agentName}`,
    },
    aiActivity: {
      list: '/admin/ai-activity',
      summary: '/admin/ai-activity/summary',
      filters: '/admin/ai-activity/filters',
      traceById: (traceId: string) => `/admin/ai-activity/traces/${traceId}`,
    },
    aiServiceUsage: {
      list: '/admin/ai-service-usage',
      summary: '/admin/ai-service-usage/summary',
      filters: '/admin/ai-service-usage/filters',
    },
    aiServiceHealth: '/admin/ai-service-health',
    schedulerConfig: {
      list: '/admin/scheduler-config',
      byKey: (key: string) => `/admin/scheduler-config/${key}`,
      runNow: (key: string) => `/admin/scheduler-config/${key}/run-now`,
      reset: (key: string) => `/admin/scheduler-config/${key}/reset`,
    },
    pciDssTask: {
      pciDssTask: '/admin/pci-dss-task',
      uniqueDomains: '/admin/pci-dss-task/unique-domains',
      uniqueRequirementTitles: '/admin/pci-dss-task/unique-requirement-titles',
      uniqueTaskTitles: '/admin/pci-dss-task/unique-task-titles',
    },
    pciDssEvidencePackTask: {
      pciDssEvidencePackTask: '/admin/pci-dss-evidence-pack-task',
      uniqueSuggestedTeams: '/admin/pci-dss-evidence-pack-task/unique-suggested-teams',
    },
    itPrivilegedOperation: {
      serverLogs: '/admin/it-privileged-operation/server-logs',
      adminSeed: '/admin/it-privileged-operation/admin-seed',
      seedAllDefaultData: '/admin/it-privileged-operation/seed-all-default-data',
      maturitySeed: '/admin/it-privileged-operation/maturity-seed',
      standardSeed: '/admin/it-privileged-operation/standard-seed',
      moduleSeed: '/admin/it-privileged-operation/module-seed',
      allowAllModulesForExistingOrganisations:
        '/admin/it-privileged-operation/allow-all-modules-for-existing-organisations',
      allowInUseStandardsForAllOrganisations:
        '/admin/it-privileged-operation/allow-in-use-standards-for-all-organisations',
      updateStandardSeed: '/admin/it-privileged-operation/update-standard-seed',
      updateStandardSeedForProd: '/admin/it-privileged-operation/update-standard-seed-for-prod',
      checkQuestionsGenerated: '/admin/it-privileged-operation/check-questions-generated',
      populateRiskTrackingId: '/admin/it-privileged-operation/populate-risk-tracking-id',
      populatePolicyTrackingId: '/admin/it-privileged-operation/populate-policy-tracking-id',
      backfillPiiMasking: '/admin/it-privileged-operation/backfill-pii-masking',
      autoCreateRepositoryFoldersForAllOrganisations:
        '/admin/it-privileged-operation/auto-create-repository-folders-for-all-organisations',
      hideRepositoriesForUnselectedStandards:
        '/admin/it-privileged-operation/hide-repositories-for-unselected-standards',
      ensureOrganisationSortableSequencesForAll:
        '/admin/it-privileged-operation/ensure-organisation-sortable-sequences',
      seedDefaultSchedulerConfig: '/admin/it-privileged-operation/seed-default-scheduler-config',
      syncPoliciesToAiForAllOrganisations: '/admin/it-privileged-operation/sync-policies-to-ai-for-all-organisations',
      backfillEvidenceDates: '/admin/it-privileged-operation/backfill-evidence-dates',
      backfillEvidenceDatesModelAssisted: '/admin/it-privileged-operation/backfill-evidence-dates/model-assisted',
      backfillAiEmbeddings: '/admin/it-privileged-operation/backfill-ai-embeddings',
      backfillAiEmbeddingsStatus: '/admin/it-privileged-operation/backfill-ai-embeddings/status',
      buildAiEmbeddingIndex: '/admin/it-privileged-operation/backfill-ai-embeddings/build-index',
      aiEvidenceHealthStatus: '/admin/it-privileged-operation/ai-evidence-health/status',
      aiEvidenceHealthRegeneratePolicies: '/admin/it-privileged-operation/ai-evidence-health/regenerate-policies',
      aiEvidenceHealthRedateEvidence: '/admin/it-privileged-operation/ai-evidence-health/redate-evidence',
      aiEvidenceHealthRedateEvidenceModelAssisted:
        '/admin/it-privileged-operation/ai-evidence-health/redate-evidence/model-assisted',
      generateStandard: {
        iso: '/admin/it-privileged-operation/generate-standard/iso',
        iso9001: '/admin/it-privileged-operation/generate-standard/iso-9001',
        iso27004: '/admin/it-privileged-operation/generate-standard/iso-27004',
        iso42001: '/admin/it-privileged-operation/generate-standard/iso-42001',
        iso27701: '/admin/it-privileged-operation/generate-standard/iso-27701',
        nist: '/admin/it-privileged-operation/generate-standard/nist',
        pciDss: '/admin/it-privileged-operation/generate-standard/pci-dss',
        soc2: '/admin/it-privileged-operation/generate-standard/soc-2',
        cps230: '/admin/it-privileged-operation/generate-standard/cps-230',
        cps234: '/admin/it-privileged-operation/generate-standard/cps-234',
        cis: '/admin/it-privileged-operation/generate-standard/cis',
        csp: '/admin/it-privileged-operation/generate-standard/csp',
        smb1001: '/admin/it-privileged-operation/generate-standard/smb-1001',
        ism2025: '/admin/it-privileged-operation/generate-standard/ism-2025',
        ism2026: '/admin/it-privileged-operation/generate-standard/ism-2026',
        ndisHighIntensity: '/admin/it-privileged-operation/generate-standard/ndis-high-intensity',
        sil: '/admin/it-privileged-operation/generate-standard/sil',
        ndisIndicators: '/admin/it-privileged-operation/generate-standard/ndis-indicators',
        cmmc: '/admin/it-privileged-operation/generate-standard/cmmc',
      },
      updateStandard: {
        cps234: '/admin/it-privileged-operation/update-standard/cps-234',
        cis: '/admin/it-privileged-operation/update-standard/cis',
        cps230: '/admin/it-privileged-operation/update-standard/cps-230',
        smb1001: '/admin/it-privileged-operation/update-standard/smb-1001',
        iso27004: '/admin/it-privileged-operation/update-standard/iso-27004',
        essential8: '/admin/it-privileged-operation/update-standard/essential-8',
        iso9001: '/admin/it-privileged-operation/update-standard/iso-9001',
        iso42001: '/admin/it-privileged-operation/update-standard/iso-42001',
        nist: '/admin/it-privileged-operation/update-standard/nist',
        pciDss: '/admin/it-privileged-operation/update-standard/pci-dss',
        iso27701: '/admin/it-privileged-operation/update-standard/iso-27701',
        ism2025: '/admin/it-privileged-operation/update-standard/ism-2025',
        soc2: '/admin/it-privileged-operation/update-standard/soc-2',
      },
      removeStandard: {
        soc2: '/admin/it-privileged-operation/remove-standard/soc-2',
      },
      pciTestingProceduresSeed: '/admin/it-privileged-operation/pci-testing-procedures-seed',
      updatePCITestingProcedures: '/admin/it-privileged-operation/update-pci-testing-procedures',
      pciControlMetaSeed: '/admin/it-privileged-operation/pci-control-meta-seed',
      updatePCIControlMeta: '/admin/it-privileged-operation/update-pci-control-meta',
      ismScopingConsiderationsSeed: '/admin/it-privileged-operation/ism-scoping-considerations-seed',
      seedDefaultLeadAssessors: '/admin/it-privileged-operation/seed-default-lead-assessors',
      seedAssessmentTypes: '/admin/it-privileged-operation/seed-assessment-types',
      assignAuditorsToAudits: '/admin/it-privileged-operation/assign-auditors-to-audits',
      deleteNonGapAudits: '/admin/it-privileged-operation/delete-non-gap-audits',
      deactivateDuplicateActiveAssessments: '/admin/it-privileged-operation/deactivate-duplicate-active-assessments',
      seedRiskAssessmentData: '/admin/it-privileged-operation/seed-risk-assessment-data',
      seedPeopleRiskAssessmentData: '/admin/it-privileged-operation/seed-people-risk-assessment-data',
      seedSAQTypes: '/admin/it-privileged-operation/seed-saq-types',
      seedSAQControlMapping: '/admin/it-privileged-operation/seed-saq-control-mapping',
      removeWrongSAQControlMapping: '/admin/it-privileged-operation/remove-wrong-saq-control-mapping',
      seedFixSoaOrganisationStandardId: '/admin/it-privileged-operation/seed-fix-soa-organisation-standard-id',
      pciTestingProcedureTargetedRiskFlagsSeed: '/admin/it-privileged-operation/pci-testing-procedure-tra-flags-seed',
      seedEmailTemplates: '/admin/it-privileged-operation/seed-email-templates',
      seedPciDssTasks: '/admin/it-privileged-operation/seed-pci-dss-tasks',
      deactivatePciDssTasks: '/admin/it-privileged-operation/deactivate-pci-dss-tasks',
      seedPciDssEvidencePacks: '/admin/it-privileged-operation/seed-pci-dss-evidence-packs',
      seedPciDssAppendixA3: '/admin/it-privileged-operation/seed-pci-dss-appendix-a3',
      seedPciDssAppendixA3TestingProcedures:
        '/admin/it-privileged-operation/seed-pci-dss-appendix-a3-testing-procedures',
      updatePciDssAppendixA3Nesting: '/admin/it-privileged-operation/update-pci-dss-appendix-a3-nesting',
      seedISMPrinciples: '/admin/it-privileged-operation/seed-ism-principles',
      createStandardFallbackLogo: '/admin/it-privileged-operation/create-standard-fallback-logo',

      seedAIRegisterTypesForAllOrganisations:
        '/admin/it-privileged-operation/seed-ai-register-types-for-all-organisations',
      seedAIRegisterPermittedDataForAllOrganisations:
        '/admin/it-privileged-operation/seed-ai-register-permitted-data-for-all-organisations',
      seedAIRegisterScreeningQuestionsForAllOrganisations:
        '/admin/it-privileged-operation/seed-ai-register-screening-questions-for-all-organisations',
      createMeetingMinuteHistoryForAllOrganisations:
        '/admin/it-privileged-operation/create-meeting-minute-history-for-all-organisations',
      createPolicyHistoryForAllOrganisations:
        '/admin/it-privileged-operation/create-policy-history-for-all-organisations',
      autoSetPolicyReviewerFromOwnerForAllOrganisations:
        '/admin/it-privileged-operation/auto-set-policy-reviewer-from-owner-for-all-organisations',
      backfillPolicyAcknowledgementHistoryForAllOrganisations:
        '/admin/it-privileged-operation/backfill-policy-acknowledgement-history-for-all-organisations',
      updatePolicyHistoryReviewerIdsForAllOrganisations:
        '/admin/it-privileged-operation/update-policy-history-reviewer-ids-for-all-organisations',
      backfillAssessmentTierMappingsDryRun: '/admin/it-privileged-operation/backfill-assessment-tier-mappings/dry-run',
      backfillAssessmentTierMappingsCommit: '/admin/it-privileged-operation/backfill-assessment-tier-mappings/commit',
      seedRiskTreatmentDecisionForAllOrganisations:
        '/admin/it-privileged-operation/seed-risk-treatment-decision-for-all-organisations',
      backfillRiskRatingValues: '/admin/it-privileged-operation/backfill-risk-rating-values',
      backfillRiskLikelihoodAndImpactValues:
        '/admin/it-privileged-operation/backfill-risk-likelihood-and-impact-values',
      seedInformationSecurityObjectiveLookupsForAllOrganisations:
        '/admin/it-privileged-operation/seed-information-security-objective-lookups-for-all-organisations',
      seedFiscalYears: '/admin/it-privileged-operation/seed-fiscal-years',
      reconcileNonConformityOfiRecordsDryRun:
        '/admin/it-privileged-operation/reconcile-non-conformity-ofi-records/dry-run',
      reconcileNonConformityOfiRecordsCommit:
        '/admin/it-privileged-operation/reconcile-non-conformity-ofi-records/commit',
      updateStandardNistIDRA10: '/admin/it-privileged-operation/update-standard/nist-id-ra-10',
      seedNistCsfTiers: '/admin/it-privileged-operation/seed-nist-csf-tiers',
      mapCmmToNistCsfTiers: '/admin/it-privileged-operation/map-cmm-to-nist-csf-tiers',
      migrateChangeManagementProcessStaffLinks:
        '/admin/it-privileged-operation/migrate-change-management-process-staff-links',
    },
    // Report
    reports: {
      comprehensive: '/admin/reports/comprehensive',
      dashboardOverview: '/admin/reports/dashboard-overview',
      userAnalytics: '/admin/reports/user-analytics',
      fileRepositoryAnalytics: '/admin/reports/file-repository-analytics',
      systemHealthAnalytics: '/admin/reports/system-health-analytics',
      signup: '/admin/signup-request/comprehensive-report',
      trends: '/admin/reports/trends',
    },
    activityReports: {
      overview: '/admin/activity-reports/overview',
      comprehensive: '/admin/activity-reports/comprehensive',
      activeUsers: '/admin/activity-reports/active-users',
      moduleUsage: '/admin/activity-reports/modules/usage',
      unusedModules: '/admin/activity-reports/modules/unused',
      licensedUnused: '/admin/activity-reports/modules/licensed-unused',
      organisations: '/admin/activity-reports/organisations',
      users: '/admin/activity-reports/users',
      trends: '/admin/activity-reports/trends',
      peakUsage: '/admin/activity-reports/peak-usage',
      export: '/admin/activity-reports/export',
    },
    // Maturity
    maturity: {
      maturityModel: '/admin/maturity-models',
      maturityModelList: '/admin/maturity-models/list',
      maturityLevel: '/admin/maturity-levels',
      maturityLevelList: '/admin/maturity-levels/list',
    },
    // Standards
    standards: {
      standards: '/admin/standards',
      standardsList: '/admin/standards/list',
      error: '/admin/standards/error-on-generation',
      isGenerating: '/admin/standards/is-generating',
      standardLevel: '/admin/standard-levels',
      standardLevelList: '/admin/standard-levels/list',
      saqType: '/admin/saq-types',
    },
    fiscalYear: '/admin/fiscal-years',
    // Security Question
    securityOption: {
      securityQuestionGroup: '/admin/security-question-group',
      securityQuestionGroupList: '/admin/security-question-group/list',
      securityQuestion: '/admin/security-question',
      securityQuestionList: '/admin/security-question/list',
    },
    // Control
    control: {
      controlGroup: '/admin/control-group',
      controlGroupList: '/admin/control-group/list',
      controlGroupHierarchy: '/admin/control-group/hierarchical-list',
      control: '/admin/control',
      controlList: '/admin/control/list',
      questionnaires: '/admin/control-questionnaire',
      generateQuestionnairesForStandard: '/admin/control-questionnaire/generate-for-standard',
      generateQuestionnairesForControl: '/admin/control-questionnaire/generate-for-control',
      controlMapping: '/admin/control-mapping',
      controlMappingUpload: '/admin/control-mapping/upload',
      controlMappingTemplate: '/admin/control-mapping/template',
      vendorBaseline: '/admin/vendor-baseline',
      pciDssControlList: '/admin/control/pci-dss/list',
    },
    subscription: {
      organisationSubscription: '/admin/subscription',
      renewSubscription: '/admin/subscription/renew',
      subscriptionType: '/admin/subscription-type',
    },
    riskAssessment: {
      systemExposure: '/admin/risk-assessment/system-exposure',
      hostingModel: '/admin/risk-assessment/hosting-model',
      dataClassification: '/admin/risk-assessment/data-classification',
      userBase: '/admin/risk-assessment/user-base',
      assessmentType: '/admin/risk-assessment/assessment-type',
      questionnaire: '/admin/risk-assessment/questionnaire',
      uploadQuestionnaire: '/admin/risk-assessment/questionnaire/upload-excel',
    },
    peopleRiskAssessment: {
      assessmentType: '/admin/people-risk-assessment/assessment-type',
      questionnaire: '/admin/people-risk-assessment/questionnaire',
      uploadQuestionnaire: '/admin/people-risk-assessment/questionnaire/upload-excel',
    },
    userManual: '/admin/user-manual',
    mcpServerExtension: '/admin/mcp-server-extension',
  },
}
export type EndPoint = (typeof endPoint)[keyof typeof endPoint]
